import React from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Utensils, AlertTriangle, ChefHat, Refrigerator } from "lucide-react";
import DictateButton from "@/components/DictateButton";

export const DIET_OPTIONS = [
  "Vegetarian", "Vegan", "Jain", "Keto", "Paleo",
  "Gluten-free", "Halal", "Kosher", "Pescatarian",
  "Dairy-free", "Low-sodium", "Ayurvedic (Sattvic)",
];

export const ALLERGY_OPTIONS = [
  "Nuts", "Peanuts", "Dairy", "Egg", "Soy",
  "Shellfish", "Fish", "Gluten / Wheat",
  "Sesame", "Mustard", "Sulphites",
];

export const KITCHEN_EQUIPMENT_OPTIONS = [
  "Gas hob", "Induction hob", "Electric oven",
  "Convection microwave", "Wood-fired oven", "Tandoor",
  "Sous-vide setup", "Deep fryer", "Charcoal grill / Mangal",
  "Steam oven", "Stand mixer / Kitchen-aid", "Food processor",
  "High-speed blender", "Ice cream churner", "Espresso machine",
  "Wine fridge / Cellar", "Walk-in cold room",
];

/**
 * Reusable chips-based multi-select + Chef Notes textarea + Kitchen Equipment checklist.
 * value = { dietary_preferences: [], allergies: [], chef_notes: "", kitchen_equipment: [] }
 */
export default function DietaryBlock({ value, onChange, testIdPrefix = "diet" }) {
  const v = value || { dietary_preferences: [], allergies: [], chef_notes: "", kitchen_equipment: [] };

  const toggle = (key, item) => {
    const arr = v[key] || [];
    const next = arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];
    onChange({ ...v, [key]: next });
  };

  return (
    <div className="space-y-8" data-testid={`${testIdPrefix}-block`}>
      {/* Dietary preferences */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Utensils size={14} className="text-[#F5B041]" />
          <Label className="small-caps text-[10px] text-neutral-400">Dietary preferences</Label>
        </div>
        <div className="flex flex-wrap gap-2">
          {DIET_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => toggle("dietary_preferences", d)}
              data-testid={`${testIdPrefix}-diet-${d.replace(/[^a-z]/gi, "").toLowerCase()}`}
              className={`chip cursor-pointer transition ${
                (v.dietary_preferences || []).includes(d)
                  ? "border-[#C04000] text-[#F5B041] bg-[#C04000]/10"
                  : "hover:border-white/20"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* Allergies */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={14} className="text-[#C04000]" />
          <Label className="small-caps text-[10px] text-neutral-400">Allergies / intolerances</Label>
        </div>
        <div className="flex flex-wrap gap-2">
          {ALLERGY_OPTIONS.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => toggle("allergies", a)}
              data-testid={`${testIdPrefix}-allergy-${a.replace(/[^a-z]/gi, "").toLowerCase()}`}
              className={`chip cursor-pointer transition ${
                (v.allergies || []).includes(a)
                  ? "border-[#C04000] text-[#C04000] bg-[#C04000]/10"
                  : "hover:border-white/20"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* Chef notes */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ChefHat size={14} className="text-[#F5B041]" />
          <Label className="small-caps text-[10px] text-neutral-400">Chef notes — dislikes & guest-specific asks</Label>
          <span className="ml-auto">
            <DictateButton
              testId={`${testIdPrefix}-dictate-chef-notes`}
              context="Household dietary notes, dislikes, allergies and guest-specific requests for a private chef."
              onText={(t) => onChange({ ...v, chef_notes: [(v.chef_notes || "").trim(), t].filter(Boolean).join(" ") })}
            />
          </span>
        </div>
        <Textarea
          data-testid={`${testIdPrefix}-chef-notes`}
          value={v.chef_notes || ""}
          onChange={(e) => onChange({ ...v, chef_notes: e.target.value })}
          placeholder="e.g. Household dislikes cilantro. Guest #2 is expecting and prefers no strong cheeses. Kids on-site prefer mild spice…"
          className="rounded-none bg-transparent border-white/10 min-h-28"
        />
      </div>

      {/* Kitchen equipment */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Refrigerator size={14} className="text-[#F5B041]" />
          <Label className="small-caps text-[10px] text-neutral-400">Kitchen equipment on-site</Label>
        </div>
        <p className="text-[11px] text-neutral-500 -mt-1 mb-3">
          Select every tool your chef will have to work with — this shapes the menu they design for you.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {KITCHEN_EQUIPMENT_OPTIONS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => toggle("kitchen_equipment", k)}
              data-testid={`${testIdPrefix}-equipment-${k.replace(/[^a-z]/gi, "").toLowerCase()}`}
              className={`text-left text-xs px-3 py-2 border transition ${
                (v.kitchen_equipment || []).includes(k)
                  ? "border-[#C04000] bg-[#C04000]/10 text-white"
                  : "border-white/10 text-neutral-400 hover:border-white/25 hover:text-white"
              }`}
            >
              <span className="inline-block w-3 h-3 mr-2 border border-white/20 align-middle text-center leading-3">
                {(v.kitchen_equipment || []).includes(k) ? "✓" : ""}
              </span>
              {k}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
